import styled from '@emotion/styled';
import { Button, Loader, Stack, Text, tokens } from '@hotjar/hotsauce';
import { useEffect, useState } from 'react';
import { useTranslation } from '@hotjar/translations';
import { FullScreenView } from '@hotjar/insights-common';
import { useSurveyTemplatesQuery } from '../../domain/SurveyTemplates/query';
import type { SurveyId } from '../../domain/SurveyTemplates/SurveyTemplate';
import { CategoriesSideBar } from '../CategoriesSideBar/CategoriesSideBar';
import { SurveyTemplatesList } from '../SurveyTemplatesList/SurveyTemplatesList';
import { useSurveyTemplateCategories } from './useSurveyTemplateCategories';
import type { CategoryInfo } from '../common/model/Category';
import { useSurveyTemplatesFromCategoryWithSearch } from './useSurveyTemplatesFromCategoryWithSearch';
import { SurveyTemplatesSearch } from '../SurveyTeplatesSearch/SurveyTemplatesSearch';
import { SurveyTemplatesEmptyState } from '../SurveyTemplatesEmptyState/SurveyTemplatesEmptyState';
import { useDebounce } from '../../hooks/useDebounce';

export interface SurveyTemplatesViewProps {
  onClose: () => void;
  onContinue: (surveyId?: SurveyId, templateCategoryTitle?: CategoryInfo['title']) => void;
  hasSurveyCreatePermission: boolean;
  trackQuery: (resultsTotal: number, searchText: string) => void;
}

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: center;
  margin-top: ${tokens.spacer.x8};
  gap: ${tokens.spacer.x2};
`;

const StyledLoader = styled(Loader)`
  margin-top: ${tokens.spacer.x8};
`;

const Wrapper = styled.div`
  padding: ${tokens.spacer.x5};
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacer.x4};
  max-width: 1600px;
  margin: auto;
`;

const Content = styled(Stack)`
  width: 100%;
`;

export const SurveyTemplatesView = ({
  onClose,
  onContinue,
  hasSurveyCreatePermission,
  trackQuery,
}: SurveyTemplatesViewProps): JSX.Element => {
  const { t } = useTranslation('survey-templates');
  const { data: result, error, isLoading } = useSurveyTemplatesQuery();
  const categories = useSurveyTemplateCategories(result?.survey_templates);
  const [selectedCategory, setSelectedCategory] = useState<CategoryInfo | undefined>(undefined);
  const [searchValue, setSearchValue] = useState<string>('');
  const templates = useSurveyTemplatesFromCategoryWithSearch(
    result?.survey_templates,
    selectedCategory,
    searchValue,
  );

  useEffect(() => {
    if (selectedCategory !== undefined || categories === undefined) {
      return;
    }
    setSelectedCategory(categories[0]);
  }, [selectedCategory, categories]);

  useDebounce(() => trackQuery(templates.length, searchValue), [searchValue], {
    timeout: 1000,
    trailingOnly: true,
  });

  return (
    <FullScreenView
      onClose={onClose}
      title={t('header.title')}
      contextActions={
        !error && (
          <Button onClick={() => onContinue()} size="s" isDisabled={!hasSurveyCreatePermission}>
            {t('actions.start-from-scratch')}
          </Button>
        )
      }
    >
      <Wrapper>
        <Stack direction="column">
          <Text as="h1" textType="header4" textColor="text_default">
            {t('body.title')}
          </Text>
          <Text as="p" textType="body2" textColor="text_light">
            {t('body.text')}
          </Text>
        </Stack>
        {error && (
          <ErrorContainer>
            <Text as="h1" textType="header4" textColor="text_default">
              {t('error.retrieving-templates.header')}
            </Text>
            <Text as="p" textType="body1" textColor="text_default">
              {t('error.retrieving-templates.body')}
            </Text>

            <Button kind="primary" onClick={() => onContinue()}>
              {t('actions.new-survey')}
            </Button>
          </ErrorContainer>
        )}
        {isLoading && <StyledLoader size="xl" />}
        {result && categories && selectedCategory && (
          <Stack direction="row" spacing="x6">
            <CategoriesSideBar
              selectedCategory={selectedCategory}
              onCategorySelected={(category) => {
                setSearchValue('');
                setSelectedCategory(category);
              }}
              categories={categories}
              suppressCategorySelection={!!searchValue}
            />
            <Content direction="column" spacing="x4">
              <SurveyTemplatesSearch
                searchValue={searchValue}
                onSearchValueChange={setSearchValue}
              />
              {templates.length ? (
                <SurveyTemplatesList
                  templates={templates}
                  onSelected={(surveyId) => onContinue(surveyId, selectedCategory.title)}
                />
              ) : (
                <SurveyTemplatesEmptyState
                  searchValue={searchValue}
                  onContinue={() => onContinue(undefined)}
                />
              )}
            </Content>
          </Stack>
        )}
      </Wrapper>
    </FullScreenView>
  );
};
